/**
 * t335u item 5: visitor sign-in, "on site now" and the fire roll call. Shared by the API, the kiosk,
 * desktop, phone web and the native app.
 */
import { DateTime } from 'luxon';

// t632u: everyone who signs in is a Visitor; there's no visitor or contractor choice any more (contractors were moved over, keeping their company).
/** Visitor photos are deleted after this many days; visitor records after VISITOR_RECORD_RETENTION_MONTHS. */
/** Who sees a building's on-site list and runs its roll call: timesheet approvers, kiosk managers, and issue settings or managers there. */
export const ON_SITE_PERMS = ['workforce.timesheet.approve', 'workforce.clock.kiosk', 'ops.incident.settings', 'ops.incident.manage'] as const;
export const VISITOR_PHOTO_RETENTION_DAYS = 30;
export const VISITOR_RECORD_RETENTION_MONTHS = 12;
/** Still signed in this long after arriving: signed out by the worker overnight so on site now stays true. */
export const VISITOR_AUTO_SIGN_OUT_HOURS = 18;

/** t627u: host_search lets the public reception page find hosts by name (names only). */
export interface VisitorSettings { location_id: string; enabled: boolean; rules: string | null; ask_photo: boolean; host_search?: boolean; updated_at: string | null }

export interface Visitor {
  id: string;
  location_id: string;
  location_name: string;
  name: string;
  company: string | null;
  host_user_id: string | null;
  host_name: string | null;
  car_reg: string | null;
  has_photo: boolean;
  rules_accepted_at: string | null;
  signed_in_at: string;
  signed_in_via: 'kiosk' | 'app' | 'phone';
  signed_in_by_name: string | null;
  signed_out_at: string | null;
  signed_out_via: 'kiosk' | 'app' | 'auto' | 'phone' | null;
  /** t340u: who signed them out in the app. */
  signed_out_by_name?: string | null;
}

export interface VisitorInput { location_id: string; name: string; company?: string | null; host_user_id?: string | null; car_reg?: string | null }

/** A building where I can see who's on site (and maybe change its visitor settings). */
export interface VisitorBuilding { id: string; name: string; can_settings: boolean; enabled: boolean; /** t535u: the person's own primary building. */ primary?: boolean }

export interface OnSitePerson {
  /** `staff:<user id>` or `visitor:<visitor id>`; the roll call uses the same keys. */
  key: string;
  kind: 'staff' | 'visitor';
  id: string;
  name: string;
  /** Staff: position or "On break"; visitors: company and who they're seeing. */
  detail: string | null;
  since: string;
  /** t340u: visitors with a photo (thumbnail beside the name). */
  has_photo?: boolean;
  /** t342u: the building, for All buildings. */
  location_name?: string;
}

export interface RollCallPerson { key: string; kind: 'staff' | 'visitor'; name: string; detail: string | null; safe_at: string | null; marked_by_name: string | null }
export interface RollCall {
  id: string;
  location_id: string;
  location_name: string;
  started_at: string;
  started_by_name: string | null;
  ended_at: string | null;
  ended_by_name: string | null;
  people: RollCallPerson[];
}
export interface RollCallSummary { id: string; started_at: string; ended_at: string | null; started_by_name: string | null; total: number; safe: number }

export interface OnSite {
  location_id: string;
  location_name: string;
  timezone: string;
  staff: OnSitePerson[];
  visitors: OnSitePerson[];
  roll_call: RollCall | null;
  last_roll_call: RollCallSummary | null;
}

/** What the kiosk needs: present only when the building has visitor sign-in switched on. */
export interface KioskVisitorConfig { rules: string | null; ask_photo: boolean }
/** A visitor on site, as the kiosk lists them for signing out: first name and initial only. */
export interface KioskVisitor { id: string; label: string; company: string | null }
export interface KioskVisitorResult { id: string; message: string; host_told: boolean }

export const onSiteKey = (kind: 'staff' | 'visitor', id: string) => `${kind}:${id}`;

/** "Jane Smith" → "Jane S." (what the kiosk shows other people). */
export function visitorLabel(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] ?? '';
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
}

/** Company and host as one line: "Acme Lifts · seeing Phoebe Mensah". */
export function visitorDetail(v: { company: string | null; host_name: string | null }): string {
  return [v.company, v.host_name ? `seeing ${v.host_name}` : null].filter(Boolean).join(' · ');
}

/** Form problems, in the order they're on the form. */
export function visitorProblem(b: { name?: string | null; company?: string | null; car_reg?: string | null }): string | null {
  const name = (b.name ?? '').trim();
  if (name.length < 2) return 'Enter your full name';
  if (name.length > 80) return 'Name is too long';
  if ((b.company ?? '').trim().length > 80) return 'Company is too long';
  if ((b.car_reg ?? '').trim().length > 12) return 'Vehicle registration is too long';
  return null;
}

/** Tidies a visitor form for the API: trimmed, blanks to null, vehicle registrations in capitals. */
export function visitorPayload(b: VisitorInput): VisitorInput {
  const t = (s?: string | null) => (s ?? '').trim() || null;
  return { location_id: b.location_id, name: (b.name ?? '').trim().replace(/\s+/g, ' '), company: t(b.company), host_user_id: b.host_user_id || null,
    car_reg: t(b.car_reg)?.toUpperCase().replace(/\s+/g, ' ') ?? null };
}

export function rollCallCounts(rc: Pick<RollCall, 'people'>): { total: number; safe: number; missing: number } {
  const safe = rc.people.filter(p => p.safe_at).length;
  return { total: rc.people.length, safe, missing: rc.people.length - safe };
}

/** Missing first (so the marshal sees who's left), staff before visitors, then by name. */
export function rollCallOrder(people: RollCallPerson[]): RollCallPerson[] {
  return [...people].sort((a, b) => Number(Boolean(a.safe_at)) - Number(Boolean(b.safe_at)) || (a.kind === b.kind ? 0 : a.kind === 'staff' ? -1 : 1) || a.name.localeCompare(b.name, 'en-GB'));
}

/** "Since 09:12" today, "Since Thu 17:40" before today, in the building's time zone. */
export function onSiteSince(iso: string, tz = 'Europe/London', now: Date = new Date()): string {
  const t = DateTime.fromISO(iso).setZone(tz);
  const today = DateTime.fromJSDate(now).setZone(tz).hasSame(t, 'day');
  return `Since ${today ? t.toFormat('HH:mm') : t.setLocale('en-GB').toFormat('ccc HH:mm')}`;
}

export function rollCallTitle(rc: { started_at: string; ended_at: string | null }, tz = 'Europe/London'): string {
  const s = DateTime.fromISO(rc.started_at).setZone(tz).setLocale('en-GB');
  return rc.ended_at ? `Roll call ${s.toFormat('d LLL, HH:mm')}` : `Roll call started ${s.toFormat('HH:mm')}`;
}

export const ON_SITE_NOTE = 'Staff clocked in here and visitors signed in and not out. In a fire, start a roll call and tick people off at the assembly point; everyone with this page open sees the same list.';

// ------------------------------------------------------------------------------------------------ t340u-t342u
/** `visitors/on-site/all` and `?location_id=` left out: every building I can see (only offered with more than one). */
export const VISITOR_LOG_PAGE = 50;

/** The visitor log's time choices; Custom range opens start and end date and time pickers. */
export type VisitorRange = 'today' | '7' | '30' | 'custom';
export const VISITOR_RANGES: { id: VisitorRange; name: string }[] = [
  { id: 'today', name: 'Today' }, { id: '7', name: 'Last 7 days' }, { id: '30', name: 'Last 30 days' }, { id: 'custom', name: 'Custom range' },
];
/** Custom range ends as local date-times ("2026-10-24T09:00"), in the building's time zone. */
export interface VisitorCustomRange { from: string; to: string }

export interface VisitorLogFilter { location_id?: string; q?: string; from_at?: string; to_at?: string; before?: string; before_id?: string; limit?: number }
export interface VisitorLogPage { visitors: Visitor[]; more: boolean }

/** A default custom range: today 00:00 to now, local. */
export function defaultVisitorCustom(tz = 'Europe/London', now: Date = new Date()): VisitorCustomRange {
  const n = DateTime.fromJSDate(now).setZone(tz);
  return { from: n.startOf('day').toFormat("yyyy-LL-dd'T'HH:mm"), to: n.toFormat("yyyy-LL-dd'T'HH:mm") };
}

/** The range as instants for the API. Today / 7 / 30 run from the start of that local day with no end. */
export function visitorRangeBounds(range: VisitorRange, tz = 'Europe/London', custom?: VisitorCustomRange | null, now: Date = new Date()): { from_at?: string; to_at?: string } {
  const n = DateTime.fromJSDate(now).setZone(tz);
  if (range === 'custom') {
    const f = custom?.from ? DateTime.fromISO(custom.from, { zone: tz }) : null;
    const t = custom?.to ? DateTime.fromISO(custom.to, { zone: tz }) : null;
    return { ...(f?.isValid ? { from_at: f.toUTC().toISO()! } : {}), ...(t?.isValid ? { to_at: t.endOf('minute').toUTC().toISO()! } : {}) };
  }
  const days = range === 'today' ? 0 : Number(range) - 1;
  return { from_at: n.minus({ days }).startOf('day').toUTC().toISO()! };
}

/** "24 Oct 09:00 – 24 Oct 17:30" for the chosen custom range (filter button and sheet). */
export function visitorCustomLabel(c: VisitorCustomRange): string {
  const f = (s: string) => { const d = DateTime.fromISO(s); return d.isValid ? d.setLocale('en-GB').toFormat('d LLL HH:mm') : '…'; };
  return `${f(c.from)} – ${f(c.to)}`;
}

/** What the search box matches: lower case, spaces dropped (so "cn21kxt" finds "CN21 KXT"). */
export const visitorSearchKey = (s: string) => s.toLowerCase().replace(/\s+/g, '');

/** "4 visitors", "1 visitor", "50+ visitors" when there are more to load. */
export function visitorCountText(n: number, more = false): string {
  return `${n}${more ? '+' : ''} ${n === 1 && !more ? 'visitor' : 'visitors'}`;
}

/** "Hafod Packaging · CN21 KXT" (log rows), with the building first for All buildings; "Visitor" when there's nothing else to say. */
export function visitorLine(v: Pick<Visitor, 'company' | 'car_reg' | 'location_name'>, withBuilding = false): string {
  return [withBuilding ? v.location_name : null, v.company, v.car_reg].filter(Boolean).join(' · ') || 'Visitor';
}

/** Initials for a visitor with no photo. */
export function visitorInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
}

/** The detail sheet's lines, the same on desktop, phone web and the app. The host's line opens their person sheet. */
export interface VisitorDetailRow { key: string; label: string; value: string; person_id?: string | null }
export function visitorDetailRows(v: Visitor, tz = 'Europe/London', now: Date = new Date()): VisitorDetailRow[] {
  const when = (iso: string) => {
    const t = DateTime.fromISO(iso).setZone(tz).setLocale('en-GB');
    return DateTime.fromJSDate(now).setZone(tz).hasSame(t, 'day') ? `Today ${t.toFormat('HH:mm')}` : t.toFormat('ccc d LLL yyyy, HH:mm');
  };
  const inBy = v.signed_in_via === 'kiosk' ? 'on the kiosk' : v.signed_in_via === 'phone' ? 'on their phone' : v.signed_in_by_name ? `by ${v.signed_in_by_name}` : 'in the app';
  const outBy = v.signed_out_via === 'auto' ? 'automatically' : v.signed_out_via === 'kiosk' ? 'on the kiosk' : v.signed_out_via === 'phone' ? 'on their phone' : v.signed_out_by_name ? `by ${v.signed_out_by_name}` : null;
  const rows: VisitorDetailRow[] = [
    ...(v.company ? [{ key: 'company', label: 'Company', value: v.company }] : []),
    ...(v.car_reg ? [{ key: 'car_reg', label: 'Vehicle registration', value: v.car_reg }] : []),
    { key: 'host', label: 'Seeing', value: v.host_name ?? 'Nobody in particular', person_id: v.host_name ? v.host_user_id : null },
    { key: 'building', label: 'Building', value: v.location_name },
    { key: 'in', label: 'Signed in', value: `${when(v.signed_in_at)} ${inBy}` },
    { key: 'out', label: 'Signed out', value: v.signed_out_at ? `${when(v.signed_out_at)}${outBy ? ` ${outBy}` : ''}` : 'Still on site' },
  ];
  if (v.rules_accepted_at) rows.push({ key: 'rules', label: 'Site rules', value: `Accepted ${when(v.rules_accepted_at)}` });
  return rows;
}

/** The phone log row's right-hand side (phone web and the app): "00:59" in, then "On site" or the time out. */
export function visitorLogTimes(v: Pick<Visitor, 'signed_in_at' | 'signed_out_at'>, tz = 'Europe/London', multiDay = false): { in: string; out: string } {
  const f = (iso: string) => DateTime.fromISO(iso).setZone(tz).setLocale('en-GB').toFormat(multiDay ? 'd LLL HH:mm' : 'HH:mm');
  return { in: f(v.signed_in_at), out: v.signed_out_at ? `Out ${f(v.signed_out_at)}` : 'On site' };
}

/** The phone filter button: "Today", "Last 7 days", or the custom range. */
export function visitorRangeLabel(range: VisitorRange, custom: VisitorCustomRange): string {
  return range === 'custom' ? visitorCustomLabel(custom) : VISITOR_RANGES.find(r => r.id === range)?.name ?? 'Today';
}

/** Load more's cursor: the last row's time and id. */
export function visitorLogCursor(page: VisitorLogPage): { before: string; before_id: string } | null {
  const last = page.more ? page.visitors[page.visitors.length - 1] : undefined;
  return last ? { before: last.signed_in_at, before_id: last.id } : null;
}

/**
 * t534u: which building Visitors & on site shows. With two or more buildings the default is All buildings ('all');
 * a building picked in the filter (kept in ?building=) still wins. With one building it is that building.
 */
export function visitorBuildingChoice(buildings: { id: string }[], picked: string | null | undefined): { id: string; all: boolean } {
  const canAll = buildings.length > 1;
  const own = buildings.find(x => x.id === picked);
  if (own) return { id: own.id, all: false };
  if (canAll) return { id: 'all', all: true };
  return { id: buildings[0]?.id ?? '', all: false };
}

/** t535u: the Building field when signing a visitor in under All buildings: the only one, else the person's primary building, else blank. */
export function signInBuildingDefault(buildings: { id: string; primary?: boolean }[]): string {
  if (buildings.length === 1) return buildings[0].id;
  return buildings.find(b => b.primary)?.id ?? '';
}
export const CHOOSE_BUILDING = 'Choose a building';
